<template>
  <div class="cr-page bq-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <LeadFormDialog
      v-model="showFormDialog"
      :mode="dialogMode"
      :lead="selectedLead"
      :default-org-name="defaultOrgName()"
      :save-fn="saveLead"
      @saved="handleChanged"
    />

    <LeadConfirmDialog
      v-model="showConfirmDialog"
      :action="confirmAction"
      :lead="selectedLead"
      :confirm-fn="confirmActionFn"
      @confirmed="handleChanged"
    />

    <BulkLeadUploadDialog
      v-model="showBulkDialog"
      @uploaded="onBulkUploaded"
    />

    <UpdateBusinessQueryStatusDialog
      v-model="showStatusDialog"
      :query="selectedQuery"
      :target-status="targetStatus"
      :saving="Boolean(updatingId)"
      @confirm="onStatusConfirm"
    />

    <v-dialog v-model="showBulkDeleteConfirm" max-width="420" persistent>
      <v-card rounded="xl">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Delete {{ selectedIds.length }} lead{{ selectedIds.length === 1 ? '' : 's' }}?
        </v-card-title>
        <v-card-text>
          The selected leads are permanently removed. This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="bulkWorking" @click="showBulkDeleteConfirm = false">
            Cancel
          </v-btn>
          <v-btn color="error" variant="flat" :loading="bulkWorking" @click="confirmBulkDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBulkStatusDialog" max-width="440" persistent>
      <v-card rounded="xl">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Change status for {{ selectedIds.length }} lead{{ selectedIds.length === 1 ? '' : 's' }}
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="bulkStatus"
            :items="bulkStatusOptions"
            item-title="title"
            item-value="value"
            label="New status"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="mb-3"
          />
          <v-textarea
            v-model="bulkComments"
            label="Comments (required)"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="bulkWorking" @click="showBulkStatusDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="bulkWorking"
            :disabled="!bulkStatus || !bulkComments.trim()"
            @click="confirmBulkStatus"
          >
            Update status
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <BusinessQueryInquiryDrawer
      v-model="showInquiryDrawer"
      :query="selectedInquiryQuery"
      @contact-email="contactLead"
      @call-lead="callLeadPhone"
      @mark-converted="markConvertedFromDrawer"
    />

    <div class="cr-panel">
      <div class="cr-panel__toolbar bq-panel__toolbar">
        <div class="cr-panel__toolbar-start">
          <span class="cr-panel__count">
            {{ totalQueries }} lead{{ totalQueries === 1 ? '' : 's' }}
          </span>
          <v-select
            v-model="statusFilter"
            :items="statusFilterOptions"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            class="bq-status-filter"
            label="Status"
          />
          <v-select
            v-model="contextFilter"
            :items="contextFilterOptions"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            class="bq-status-filter"
            label="Product"
            :loading="contextsLoading"
          />
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            class="text-none bq-advanced-toggle"
            :prepend-icon="showAdvancedFilters ? 'mdi-filter-off-outline' : 'mdi-filter-variant'"
            @click="toggleAdvancedFilters"
          >
            Advanced filters
            <span v-if="activeAdvancedFilterCount" class="bq-advanced-toggle__count">
              {{ activeAdvancedFilterCount }}
            </span>
          </v-btn>
          <v-chip
            v-if="uploadIdFilter"
            size="small"
            closable
            color="primary"
            variant="tonal"
            @click:close="removeUploadFilter"
          >
            Bulk upload leads
          </v-chip>
        </div>
        <BusinessQueriesHeader
          :loading="loading"
          :is-session-ready="isSessionReady"
          :is-search-active="isSearchActive"
          @add="openCreate"
          @bulk-upload="showBulkDialog = true"
          @upload-history="goToUploadHistory"
          @refresh="handleRefresh"
        />
      </div>

      <div v-if="showAdvancedFilters" class="bq-advanced">
        <div class="bq-advanced__head">
          <div>
            <p class="bq-advanced__title">Advanced filters</p>
            <p class="bq-advanced__hint">
              Local filters only — same field is OR, different fields are AND.
            </p>
          </div>
          <div class="bq-advanced__actions">
            <v-btn
              variant="text"
              size="small"
              class="text-none"
              @click="addAdvancedFilterRow"
            >
              Add filter
            </v-btn>
            <v-btn
              variant="text"
              size="small"
              class="text-none"
              :disabled="!advancedFilters.length"
              @click="clearAdvancedFilters"
            >
              Clear
            </v-btn>
          </div>
        </div>

        <div v-if="advancedFilters.length" class="bq-advanced__rows">
          <div
            v-for="(filter, index) in advancedFilters"
            :key="filter.id"
            class="bq-advanced__row"
            :class="{ 'bq-advanced__row--range': isRangeOperator(filter.operator) }"
          >
            <v-select
              v-model="filter.field"
              :items="advancedFilterFieldOptions"
              item-title="label"
              item-value="key"
              placeholder="Field"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="onAdvancedFilterFieldChange(filter)"
            />
            <v-select
              v-model="filter.operator"
              :items="operatorsForAdvancedRow(filter)"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="onAdvancedFilterOperatorChange(filter)"
            />

            <div v-if="isRangeOperator(filter.operator)" class="bq-advanced__range">
              <v-text-field
                v-if="advancedFilterRowKind(filter) === 'date'"
                :model-value="getFilterDateInputValue(getFilterRangeMin(filter))"
                type="date"
                placeholder="From"
                variant="outlined"
                density="compact"
                hide-details
                @update:model-value="setFilterRangeMin(filter, $event)"
              />
              <v-text-field
                v-else
                :model-value="getFilterRangeMin(filter)"
                type="number"
                placeholder="Min"
                variant="outlined"
                density="compact"
                hide-details
                @update:model-value="setFilterRangeMin(filter, $event)"
              />
              <v-text-field
                v-if="advancedFilterRowKind(filter) === 'date'"
                :model-value="getFilterDateInputValue(getFilterRangeMax(filter))"
                type="date"
                placeholder="To"
                variant="outlined"
                density="compact"
                hide-details
                @update:model-value="setFilterRangeMax(filter, $event)"
              />
              <v-text-field
                v-else
                :model-value="getFilterRangeMax(filter)"
                type="number"
                placeholder="Max"
                variant="outlined"
                density="compact"
                hide-details
                @update:model-value="setFilterRangeMax(filter, $event)"
              />
            </div>
            <v-select
              v-else-if="isMultiOperator(filter.operator) && usesAdvancedValueSelect(filter)"
              :model-value="filter.values"
              :items="advancedValueOptions(filter)"
              multiple
              chips
              closable-chips
              placeholder="Select values"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="filter.values = $event || []"
            />
            <v-combobox
              v-else-if="isMultiOperator(filter.operator)"
              :model-value="filter.values"
              multiple
              chips
              closable-chips
              placeholder="Values"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="filter.values = $event || []"
            />
            <v-text-field
              v-else-if="advancedFilterRowKind(filter) === 'date'"
              :model-value="getFilterDateInputValue(getFilterSingleValue(filter))"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="setFilterSingleValue(filter, $event)"
            />
            <v-text-field
              v-else-if="advancedFilterRowKind(filter) === 'number'"
              :model-value="getFilterSingleValue(filter)"
              type="number"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="setFilterSingleValue(filter, $event)"
            />
            <v-select
              v-else-if="usesAdvancedValueSelect(filter)"
              :model-value="getFilterSingleValue(filter)"
              :items="advancedValueOptions(filter)"
              placeholder="Select value"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              no-data-text="No values found for this field"
              @update:model-value="setFilterSingleValue(filter, $event)"
            />
            <v-text-field
              v-else
              :model-value="getFilterSingleValue(filter)"
              placeholder="Value"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="setFilterSingleValue(filter, $event)"
            />

            <v-btn
              icon
              variant="text"
              size="small"
              @click="removeAdvancedFilterRow(index)"
            >
              <v-icon size="18">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <div v-if="selectedIds.length" class="bq-bulk-bar">
        <span class="bq-bulk-bar__count">
          {{ selectedIds.length }} selected
        </span>
        <v-btn
          variant="text"
          size="small"
          prepend-icon="mdi-swap-horizontal"
          @click="showBulkStatusDialog = true"
        >
          Change status
        </v-btn>
        <v-btn
          variant="text"
          size="small"
          color="error"
          prepend-icon="mdi-trash-can-outline"
          @click="showBulkDeleteConfirm = true"
        >
          Delete
        </v-btn>
        <v-spacer />
        <v-btn variant="text" size="small" @click="clearSelection">
          Clear
        </v-btn>
      </div>

      <BusinessQueriesList
        :queries="paginatedQueries"
        :loading="loading"
        :is-search-active="isSearchActive"
        :selected-ids="selectedIds"
        :columns="tableFieldColumns"
        :status-options="contextStatusOptions"
        :status-options-for="statusOptionsForQuery"
        @toggle-select="toggleSelect"
        @toggle-select-all="toggleSelectAll"
        @update-status="openStatusDialog"
        @view-inquiry="viewInquiry"
        @contact-lead="contactLead"
        @edit="openEdit"
        @enable="openEnable"
        @disable="openDisable"
        @delete="openDelete"
      />

      <PortalListPagination
        class="cr-panel__footer"
        :page="page"
        :page-count="pageCount"
        :items-per-page="itemsPerPage"
        :range-label="rangeLabel"
        :total="filteredQueries.length"
        @update:page="setPage"
        @update:items-per-page="setItemsPerPage"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import BusinessQueriesHeader from '@/components/BusinessQueries/BusinessQueriesHeader.vue'
import BusinessQueriesList from '@/components/BusinessQueries/BusinessQueriesList.vue'
import UpdateBusinessQueryStatusDialog from '@/components/BusinessQueries/UpdateBusinessQueryStatusDialog.vue'
import BusinessQueryInquiryDrawer from '@/components/BusinessQueries/BusinessQueryInquiryDrawer.vue'
import LeadFormDialog from '@/components/BusinessQueries/Dialogs/LeadFormDialog.vue'
import LeadConfirmDialog from '@/components/BusinessQueries/Dialogs/LeadConfirmDialog.vue'
import BulkLeadUploadDialog from '@/components/BusinessQueries/Dialogs/BulkLeadUploadDialog.vue'
import { useBusinessQueriesData } from '@/composables/businessQueries/useBusinessQueriesData'
import { buildFieldDefsFromDefinitions } from '@/utils/leadFields'
import {
  buildFilterValueOptions,
  campaignFilterFieldKind,
  findCampaignFilterFieldDef,
  getFilterDateInputValue,
  getFilterRangeMax,
  getFilterRangeMin,
  getFilterSingleValue,
  isSelectFilterField,
  operatorsForFilterField,
  setFilterRangeMax,
  setFilterRangeMin,
  setFilterSingleValue,
} from '@/utils/campaignFilters'

defineOptions({ name: 'BusinessQueriesPage' })

const router = useRouter()
const store = useStore()
const snackbar = ref({})
const showStatusDialog = ref(false)
const showInquiryDrawer = ref(false)
const showFormDialog = ref(false)
const showConfirmDialog = ref(false)
const showBulkDialog = ref(false)
const selectedQuery = ref(null)
const selectedInquiryQuery = ref(null)
const selectedLead = ref(null)
const targetStatus = ref('')
const dialogMode = ref('CREATE')
const confirmAction = ref('delete')
const selectedIds = ref([])
const showBulkDeleteConfirm = ref(false)
const showBulkStatusDialog = ref(false)
const bulkStatus = ref('')
const bulkComments = ref('')
const bulkWorking = ref(false)

const {
  loading,
  updatingId,
  page,
  itemsPerPage,
  pageCount,
  statusFilter,
  statusFilterOptions,
  contextStatusOptions,
  statusOptionsForQuery,
  contextFilter,
  contextFilterOptions,
  contextsLoading,
  activeContext,
  uploadIdFilter,
  clearUploadFilter,
  advancedFilters,
  showAdvancedFilters,
  advancedFilterFieldOptions,
  activeAdvancedFilterCount,
  addAdvancedFilterRow,
  removeAdvancedFilterRow,
  clearAdvancedFilters,
  toggleAdvancedFilters,
  isSessionReady,
  queries,
  filteredQueries,
  paginatedQueries,
  totalQueries,
  rangeLabel,
  isSearchActive,
  loadQueries,
  saveLead,
  changeQueryStatus,
  enableLeadRecord,
  disableLeadRecord,
  deleteLeadRecord,
  bulkDeleteLeadRecords,
  bulkChangeStatus,
  defaultOrgName,
  setPage,
  setItemsPerPage,
} = useBusinessQueriesData()

function advancedFilterRowKind(filter) {
  const def = findCampaignFilterFieldDef(
    advancedFilterFieldOptions.value,
    filter.field,
  )
  return campaignFilterFieldKind(def)
}

function advancedFilterFieldDef(filter) {
  return findCampaignFilterFieldDef(
    advancedFilterFieldOptions.value,
    filter.field,
  )
}

function usesAdvancedValueSelect(filter) {
  return isSelectFilterField(advancedFilterFieldDef(filter))
}

function advancedValueOptions(filter) {
  return buildFilterValueOptions(advancedFilterFieldDef(filter), queries.value)
}

function operatorsForAdvancedRow(filter) {
  const def = findCampaignFilterFieldDef(
    advancedFilterFieldOptions.value,
    filter.field,
  )
  return operatorsForFilterField(def)
}

function isRangeOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'BETWEEN'
}

function isMultiOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'IN'
}

function onAdvancedFilterFieldChange(filter) {
  const ops = operatorsForAdvancedRow(filter)
  if (!ops.some((op) => op.value === filter.operator)) {
    filter.operator = ops[0]?.value || 'EQUALS'
  }
  filter.values = []
}

function onAdvancedFilterOperatorChange(filter) {
  if (isRangeOperator(filter.operator)) {
    filter.values = [getFilterRangeMin(filter), getFilterRangeMax(filter)]
    return
  }
  if (isMultiOperator(filter.operator)) {
    filter.values = Array.isArray(filter.values) ? filter.values : []
    return
  }
  const single = getFilterSingleValue(filter)
  filter.values = single === '' || single == null ? [] : [single]
}

/** Table headers follow the active context's custom_field_definitions. */
const tableFieldColumns = computed(() => {
  const contextDefs = activeContext.value?.custom_field_definitions
  if (contextDefs && typeof contextDefs === 'object' && Object.keys(contextDefs).length) {
    return buildFieldDefsFromDefinitions(contextDefs)
  }
  return buildFieldDefsFromDefinitions(
    store.state.organization?.default_field_definitions,
  )
})

// Bulk status changes validate against the active context's own statuses.
const bulkStatusOptions = computed(() => contextStatusOptions.value)

const confirmActionFn = computed(() => {
  if (confirmAction.value === 'enable') return enableLeadRecord
  if (confirmAction.value === 'disable') return disableLeadRecord
  return deleteLeadRecord
})

function showSnack(type, message) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: type === 'error' ? 'error' : 'success',
    SnackbarText: message,
  }
}

async function handleRefresh() {
  const result = await loadQueries()
  if (!result.ok && result.error) {
    showSnack('error', result.error)
  }
}

// Own mutations patch the in-memory shard data optimistically — no refetch
// here (shard sync lags a few seconds and would make the UI feel broken).
function handleChanged() {
  showInquiryDrawer.value = false
  selectedIds.value = []
}

function toggleSelect(query) {
  const id = String(query?.requestId || '').trim()
  if (!id) return
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter((item) => item !== id)
    : [...selectedIds.value, id]
}

function toggleSelectAll() {
  const pageIds = paginatedQueries.value
    .map((query) => String(query.requestId || '').trim())
    .filter(Boolean)
  const allSelected = pageIds.every((id) => selectedIds.value.includes(id))
  selectedIds.value = allSelected
    ? selectedIds.value.filter((id) => !pageIds.includes(id))
    : [...new Set([...selectedIds.value, ...pageIds])]
}

function clearSelection() {
  selectedIds.value = []
}

async function confirmBulkDelete() {
  bulkWorking.value = true
  try {
    const result = await bulkDeleteLeadRecords(selectedIds.value)
    if (!result.ok) {
      showSnack('error', result.error)
      return
    }
    showSnack('success', result.message)
    showBulkDeleteConfirm.value = false
    selectedIds.value = []
  } finally {
    bulkWorking.value = false
  }
}

async function confirmBulkStatus() {
  bulkWorking.value = true
  try {
    const result = await bulkChangeStatus(
      selectedIds.value,
      bulkStatus.value,
      bulkComments.value,
    )
    if (!result.ok) {
      showSnack('error', result.error)
      return
    }
    showSnack('success', result.message)
    showBulkStatusDialog.value = false
    bulkStatus.value = ''
    bulkComments.value = ''
    selectedIds.value = []
  } finally {
    bulkWorking.value = false
  }
}

function goToUploadHistory() {
  router.push({ name: 'BulkUploadHistory' })
}

function removeUploadFilter() {
  clearUploadFilter()
  // Drop the query param so a refresh doesn't re-apply the filter.
  void router.replace({ name: 'BusinessQueries' })
}

function onBulkUploaded() {
  showSnack('success', 'CSV uploaded. Processing starts shortly.')
  void loadQueries()
  goToUploadHistory()
}

function openCreate() {
  selectedLead.value = null
  dialogMode.value = 'CREATE'
  showFormDialog.value = true
}

function openEdit(lead) {
  selectedLead.value = lead ? { ...lead } : null
  dialogMode.value = 'EDIT'
  showInquiryDrawer.value = false
  showFormDialog.value = true
}

function openEnable(lead) {
  selectedLead.value = lead ? { ...lead } : null
  confirmAction.value = 'enable'
  showConfirmDialog.value = true
}

function openDisable(lead) {
  selectedLead.value = lead ? { ...lead } : null
  confirmAction.value = 'disable'
  showConfirmDialog.value = true
}

function openDelete(lead) {
  selectedLead.value = lead ? { ...lead } : null
  confirmAction.value = 'delete'
  showConfirmDialog.value = true
}

function openStatusDialog(payload) {
  selectedQuery.value = payload?.query || null
  targetStatus.value = String(payload?.requestStatus || '').toUpperCase()
  showStatusDialog.value = true
}

function viewInquiry(query) {
  selectedInquiryQuery.value = query ? { ...query } : null
  showInquiryDrawer.value = Boolean(query)
}

function contactLead(query) {
  const email = String(query?.email || '').trim()
  if (!email || email === '—') {
    showSnack('error', 'No contact email available for this lead')
    return
  }
  window.location.href = `mailto:${encodeURIComponent(email)}`
}

function callLeadPhone(query) {
  // .phone first, .phoneRaw as fallback -- .phone is formatPhoneNumber()'s
  // output, which reconstructs a full dialable number (with country code)
  // even when the stored raw value is missing one. Dialing phoneRaw first
  // meant a lead stored without a country code silently placed a call to
  // an incomplete number. Same fix as CampaignDetailPage.vue's dialer.
  const phone = String(query?.phone || query?.phoneRaw || '').trim()
  if (!phone) {
    showSnack('error', 'No contact phone available for this lead')
    return
  }
  window.location.href = `tel:${phone.replace(/\s+/g, '')}`
}

function markConvertedFromDrawer(query) {
  showInquiryDrawer.value = false
  openStatusDialog({
    query,
    requestStatus: 'CONVERTED',
  })
}

async function onStatusConfirm({ requestId, requestStatus, comments }) {
  const result = await changeQueryStatus(requestId, requestStatus, comments)
  if (!result.ok) {
    showSnack('error', result.error)
    return
  }
  showStatusDialog.value = false
  showInquiryDrawer.value = false
  selectedQuery.value = null
  selectedInquiryQuery.value = null
  targetStatus.value = ''
  // changeQueryStatus already patched the in-memory record — no refetch.
  showSnack('success', result.message || 'Status updated successfully')
}
</script>
